/**
 * Bespoke sub-panels that sit below the generic SchemaForm for primitives
 * whose configuration is richer than "render my input schema":
 *
 *  - Input (polymorphic): subtype picker + slot editor
 *  - EXTRACT: chip grid of built-in entities + inline custom entity cards
 *  - OUTPUT_DOCUMENT: xlsx/csv columns editor
 *  - OUTPUT_INTEGRATION: integration type picker + multi-add actions with
 *    capability-specific sub-forms
 */

import React from 'react';
import clsx from 'clsx';
import {
  ArrowsUpDownIcon,
  BuildingLibraryIcon,
  InformationCircleIcon,
  PlusIcon,
  TrashIcon
} from '@heroicons/react/24/outline';

import { useBuilder } from '~/pages/workflows/builder/state/context';
import {
  INPUT_SUBTYPES,
  EXTRACT_BUILTIN_ENTITIES,
  getPrimitive
} from '~/pages/workflows/builder/fixtures/registry';
import {
  INTEGRATION_TYPES,
  INTEGRATION_BY_KEY
} from '~/pages/workflows/builder/fixtures/capabilities';
import {
  firstEnumerableBinding,
  hasUpstreamList,
  outputRunMode
} from '~/pages/workflows/builder/state/selectors';
import type {
  DefinitionNode,
  InputSubtype,
  WorkflowDefinitionInputSlot
} from '~/pages/workflows/builder/types';
import { INPUT_SUBTYPE_ICON } from '~/pages/workflows/builder/constants';
import { BooleanInput, ChipMultiSelect, FieldRow, NumberInput, Select, TextInput } from './fields';

export function InputNodeInspector(props: { node: DefinitionNode }) {
  const { state, dispatch } = useBuilder();
  const { node } = props;
  const slot = state.inputs.find((s) => s.key === node.ownedSlotKey);

  return (
    <div className="space-y-3">
      <FieldRow label="Input type" required>
        <div className="grid grid-cols-2 gap-1">
          {INPUT_SUBTYPES.map((st) => {
            const Icon = INPUT_SUBTYPE_ICON[st.key];
            const active = node.inputSubtype === st.key;
            return (
              <button
                key={st.key}
                type="button"
                onClick={() =>
                  dispatch({
                    type: 'set-input-subtype',
                    nodeId: node.id,
                    subtype: st.key as InputSubtype
                  })
                }
                className={clsx(
                  'flex items-start gap-1.5 rounded-md border px-2 py-1.5 text-left text-xs',
                  active
                    ? 'border-primary-400 bg-primary-50 dark:bg-primary-500/10 text-primary-700 dark:text-primary-300'
                    : 'border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 text-neutral-700 dark:text-neutral-300 hover:border-primary-300 dark:hover:border-primary-500/30'
                )}
              >
                <Icon className="mt-0.5 h-3.5 w-3.5 flex-none" />
                <div>
                  <div className="font-semibold">{st.label}</div>
                  <div className="text-[10px] leading-tight text-neutral-500 dark:text-neutral-400 line-clamp-2">
                    {st.description}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </FieldRow>

      {!node.inputSubtype && (
        <div className="rounded-md bg-amber-50 dark:bg-amber-500/10 p-2 text-xs text-amber-700 dark:text-amber-300">
          Pick an input type to reveal the configuration.
        </div>
      )}

      {node.inputSubtype && slot && <SlotEditor slot={slot} />}

      {node.inputSubtype === 'web' && (
        <div className="rounded-md bg-neutral-50 dark:bg-neutral-900 p-2 text-[11px] text-neutral-500 dark:text-neutral-400">
          Web lookups don't have a runtime input slot — the query comes from an upstream step or a
          static value.
        </div>
      )}
    </div>
  );
}

function SlotEditor(props: { slot: WorkflowDefinitionInputSlot }) {
  const { dispatch } = useBuilder();
  const { slot } = props;
  return (
    <div className="rounded-md border border-neutral-200 dark:border-neutral-700 bg-neutral-50/50 dark:bg-neutral-900/50 p-3 space-y-2">
      <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-neutral-500 dark:text-neutral-400">
        <BuildingLibraryIcon className="h-3 w-3" />
        Runtime input slot
      </div>
      <FieldRow label="Label" description="Shown on the Run form">
        <TextInput
          value={slot.label}
          onChange={(v) =>
            dispatch({ type: 'update-slot', slotKey: slot.key, patch: { label: v } })
          }
        />
      </FieldRow>
      <FieldRow
        label="Accepts multiple values"
        description="Enumerable inputs feed fan-out downstream"
      >
        <BooleanInput
          value={slot.enumerable}
          onChange={(v) =>
            dispatch({ type: 'update-slot', slotKey: slot.key, patch: { enumerable: v } })
          }
        />
      </FieldRow>
      <FieldRow label="Required">
        <BooleanInput
          value={slot.required}
          onChange={(v) =>
            dispatch({ type: 'update-slot', slotKey: slot.key, patch: { required: v } })
          }
        />
      </FieldRow>
      {slot.kind === 'FILE' && (
        <FieldRow label="Accepted file types" description="MIME types, e.g. application/pdf">
          <ChipMultiSelect
            value={slot.acceptedMimeTypes}
            onChange={(v) =>
              dispatch({ type: 'update-slot', slotKey: slot.key, patch: { acceptedMimeTypes: v } })
            }
            placeholder="application/pdf"
          />
        </FieldRow>
      )}
    </div>
  );
}

// ----- EXTRACT -----

interface EntitySpec {
  kind: 'builtin' | 'custom';
  outputKey: string;
  label: string;
  prompt?: string;
  schema?: string;
}

export function ExtractInspector(props: { node: DefinitionNode }) {
  const { dispatch } = useBuilder();
  const entities = (props.node.config.entities as EntitySpec[] | undefined) ?? [];
  const [customOpen, setCustomOpen] = React.useState(false);

  const toggleBuiltin = (outputKey: string, label: string) => {
    const exists = entities.find((e) => e.outputKey === outputKey && e.kind === 'builtin');
    if (exists) {
      dispatch({
        type: 'patch-config',
        nodeId: props.node.id,
        patch: {
          entities: entities.filter((e) => !(e.outputKey === outputKey && e.kind === 'builtin'))
        }
      });
    } else {
      dispatch({
        type: 'patch-config',
        nodeId: props.node.id,
        patch: { entities: [...entities, { kind: 'builtin', outputKey, label }] }
      });
    }
  };

  const updateCustom = (index: number, patch: Partial<EntitySpec>) => {
    const next = entities.map((e, i) => (i === index ? { ...e, ...patch } : e));
    dispatch({ type: 'patch-config', nodeId: props.node.id, patch: { entities: next } });
  };

  const removeEntity = (index: number) => {
    const next = entities.filter((_, i) => i !== index);
    dispatch({ type: 'patch-config', nodeId: props.node.id, patch: { entities: next } });
  };

  const addCustom = () => {
    const existing = entities.filter((e) => e.kind === 'custom').length;
    const outputKey = `custom_${existing + 1}`;
    dispatch({
      type: 'patch-config',
      nodeId: props.node.id,
      patch: {
        entities: [
          ...entities,
          { kind: 'custom', outputKey, label: `Custom ${existing + 1}`, prompt: '', schema: '' }
        ]
      }
    });
    setCustomOpen(true);
  };

  return (
    <div className="space-y-3">
      <FieldRow
        label="Entities to extract"
        description="Pick what to pull out. Each becomes an output port."
      >
        <div className="flex flex-wrap gap-1">
          {EXTRACT_BUILTIN_ENTITIES.map((ent) => {
            const selected = entities.some(
              (e) => e.kind === 'builtin' && e.outputKey === ent.value
            );
            return (
              <button
                key={ent.value}
                type="button"
                onClick={() => toggleBuiltin(ent.value, ent.label)}
                className={clsx(
                  'rounded-full px-2 py-0.5 text-[11px] ring-1 transition',
                  selected
                    ? 'bg-primary-600 text-white ring-primary-600'
                    : 'bg-white dark:bg-neutral-800 text-neutral-700 dark:text-neutral-300 ring-neutral-200 dark:ring-neutral-700 hover:ring-primary-300 dark:hover:ring-primary-500/30'
                )}
              >
                {ent.label}
              </button>
            );
          })}
        </div>
      </FieldRow>

      {entities.filter((e) => e.kind === 'custom').length > 0 && (
        <div className="space-y-2">
          <div className="text-[11px] font-semibold uppercase tracking-wide text-neutral-500 dark:text-neutral-400">
            Custom extractions
          </div>
          {entities.map((e, i) =>
            e.kind === 'custom' ? (
              <CustomEntityCard
                key={`${e.outputKey}-${i}`}
                entity={e}
                onChange={(patch) => updateCustom(i, patch)}
                onRemove={() => removeEntity(i)}
              />
            ) : null
          )}
        </div>
      )}

      <button
        type="button"
        onClick={addCustom}
        className="inline-flex items-center gap-1 rounded-md border border-dashed border-neutral-300 dark:border-neutral-600 px-2 py-1 text-xs text-neutral-600 dark:text-neutral-400 hover:border-primary-400 hover:text-primary-700 dark:hover:text-primary-300"
      >
        <PlusIcon className="h-3 w-3" />
        Add custom extraction
      </button>
      {customOpen && (
        <div className="rounded-md bg-amber-50 dark:bg-amber-500/10 p-2 text-[11px] text-amber-700 dark:text-amber-300 flex items-center gap-1.5">
          <InformationCircleIcon className="h-3 w-3" />
          Custom extractions use an LLM — results may vary.
        </div>
      )}
    </div>
  );
}

function CustomEntityCard(props: {
  entity: EntitySpec;
  onChange: (patch: Partial<EntitySpec>) => void;
  onRemove: () => void;
}) {
  return (
    <div className="rounded-md border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 p-3 space-y-2">
      <div className="flex items-center justify-between">
        <FieldRow label="Output name">
          <TextInput
            value={props.entity.outputKey}
            onChange={(v) => props.onChange({ outputKey: v.replace(/[^a-zA-Z0-9_]/g, '_') })}
          />
        </FieldRow>
        <button
          type="button"
          onClick={props.onRemove}
          className="ml-2 rounded p-1 text-neutral-400 dark:text-neutral-500 hover:text-rose-600 dark:hover:text-rose-400"
        >
          <TrashIcon className="h-3.5 w-3.5" />
        </button>
      </div>
      <FieldRow label="Label">
        <TextInput value={props.entity.label} onChange={(v) => props.onChange({ label: v })} />
      </FieldRow>
      <FieldRow label="Prompt" description="What to extract, in plain English">
        <TextInput
          value={props.entity.prompt}
          onChange={(v) => props.onChange({ prompt: v })}
          multiline
        />
      </FieldRow>
      <FieldRow label="JSON Schema (optional)" description="Constrains the output shape">
        <TextInput
          value={props.entity.schema}
          onChange={(v) => props.onChange({ schema: v })}
          multiline
        />
      </FieldRow>
    </div>
  );
}

// ----- OUTPUT_DOCUMENT columns editor -----

interface ColumnSpec {
  header: string;
  path: string;
}

export function DocumentColumnsEditor(props: { node: DefinitionNode }) {
  const { dispatch } = useBuilder();
  const columns = (props.node.config.columns as ColumnSpec[] | undefined) ?? [];
  const format = (props.node.config.format as string) ?? 'pdf';
  if (format !== 'xlsx' && format !== 'csv') return null;

  const update = (index: number, patch: Partial<ColumnSpec>) => {
    const next = columns.map((c, i) => (i === index ? { ...c, ...patch } : c));
    dispatch({ type: 'patch-config', nodeId: props.node.id, patch: { columns: next } });
  };
  const add = () =>
    dispatch({
      type: 'patch-config',
      nodeId: props.node.id,
      patch: { columns: [...columns, { header: '', path: '' }] }
    });
  const remove = (index: number) =>
    dispatch({
      type: 'patch-config',
      nodeId: props.node.id,
      patch: { columns: columns.filter((_, i) => i !== index) }
    });
  const move = (index: number, dir: -1 | 1) => {
    const next = [...columns];
    const target = index + dir;
    if (target < 0 || target >= next.length) return;
    [next[index], next[target]] = [next[target], next[index]];
    dispatch({ type: 'patch-config', nodeId: props.node.id, patch: { columns: next } });
  };

  return (
    <div className="mt-2 space-y-2">
      <div className="text-[11px] font-semibold uppercase tracking-wide text-neutral-500 dark:text-neutral-400">
        Columns
      </div>
      {columns.map((col, i) => (
        <div key={i} className="flex items-start gap-1">
          <div className="flex-1 grid grid-cols-2 gap-1">
            <TextInput
              value={col.header}
              onChange={(v) => update(i, { header: v })}
              placeholder="Header"
            />
            <TextInput
              value={col.path}
              onChange={(v) => update(i, { path: v })}
              placeholder="path.to.field"
            />
          </div>
          <div className="flex flex-col">
            <button
              type="button"
              onClick={() => move(i, -1)}
              className="rounded p-0.5 text-neutral-400 dark:text-neutral-500 hover:text-neutral-700 dark:hover:text-neutral-300"
              title="Move up"
            >
              <ArrowsUpDownIcon className="h-3 w-3" />
            </button>
            <button
              type="button"
              onClick={() => remove(i)}
              className="rounded p-0.5 text-neutral-400 dark:text-neutral-500 hover:text-rose-600 dark:hover:text-rose-400"
              title="Remove"
            >
              <TrashIcon className="h-3 w-3" />
            </button>
          </div>
        </div>
      ))}
      <button
        type="button"
        onClick={add}
        className="inline-flex items-center gap-1 rounded-md border border-dashed border-neutral-300 dark:border-neutral-600 px-2 py-1 text-xs text-neutral-600 dark:text-neutral-400 hover:border-primary-400 hover:text-primary-700 dark:hover:text-primary-300"
      >
        <PlusIcon className="h-3 w-3" /> Add column
      </button>
    </div>
  );
}

// ----- OUTPUT_INTEGRATION actions -----

interface ActionSpec {
  capabilityKey: string;
  label?: string;
  config: Record<string, unknown>;
  dataPath?: string;
}

export function IntegrationInspector(props: { node: DefinitionNode }) {
  const { dispatch } = useBuilder();
  const integrationType = (props.node.config.integrationType as string | undefined) ?? '';
  const actions = (props.node.config.actions as ActionSpec[] | undefined) ?? [];
  const selectedIntegration = INTEGRATION_BY_KEY[integrationType];

  const updateAction = (index: number, patch: Partial<ActionSpec>) => {
    const next = actions.map((a, i) => (i === index ? { ...a, ...patch } : a));
    dispatch({ type: 'patch-config', nodeId: props.node.id, patch: { actions: next } });
  };
  const addAction = (capabilityKey: string) => {
    dispatch({
      type: 'patch-config',
      nodeId: props.node.id,
      patch: {
        actions: [
          ...actions,
          {
            capabilityKey,
            label: selectedIntegration?.capabilities.find((c) => c.key === capabilityKey)?.label,
            config: {}
          }
        ]
      }
    });
  };
  const removeAction = (index: number) => {
    dispatch({
      type: 'patch-config',
      nodeId: props.node.id,
      patch: { actions: actions.filter((_, i) => i !== index) }
    });
  };

  return (
    <div className="space-y-3">
      <FieldRow label="Integration" required>
        <Select
          value={integrationType}
          onChange={(v) =>
            dispatch({
              type: 'patch-config',
              nodeId: props.node.id,
              patch: { integrationType: v, actions: [] }
            })
          }
          options={INTEGRATION_TYPES.map((i) => ({ value: i.key, label: i.label }))}
        />
      </FieldRow>

      {selectedIntegration && (
        <>
          <div className="space-y-2">
            <div className="text-[11px] font-semibold uppercase tracking-wide text-neutral-500 dark:text-neutral-400">
              Actions
            </div>
            {actions.length === 0 && (
              <div className="rounded-md bg-neutral-50 dark:bg-neutral-900 p-2 text-xs italic text-neutral-400 dark:text-neutral-500">
                No actions yet — add one below.
              </div>
            )}
            {actions.map((a, i) => {
              const capability = selectedIntegration.capabilities.find(
                (c) => c.key === a.capabilityKey
              );
              return (
                <div
                  key={i}
                  className="rounded-md border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 p-3 space-y-2"
                >
                  <div className="flex items-center justify-between">
                    <div className="text-xs font-semibold">
                      {capability?.label ?? a.capabilityKey}
                    </div>
                    <button
                      type="button"
                      onClick={() => removeAction(i)}
                      className="rounded p-1 text-neutral-400 dark:text-neutral-500 hover:text-rose-600 dark:hover:text-rose-400"
                    >
                      <TrashIcon className="h-3.5 w-3.5" />
                    </button>
                  </div>
                  {capability?.description && (
                    <p className="text-[11px] text-neutral-500 dark:text-neutral-400">
                      {capability.description}
                    </p>
                  )}
                  <FieldRow label="Action label (optional)">
                    <TextInput
                      value={a.label}
                      onChange={(v) => updateAction(i, { label: v })}
                      placeholder="Used for the output port label"
                    />
                  </FieldRow>
                  {capability?.configFields.map((f) => (
                    <FieldRow
                      key={f.key}
                      label={f.label}
                      description={f.description}
                      required={f.required}
                    >
                      <CapabilityField
                        field={f}
                        value={a.config[f.key]}
                        onChange={(v) => updateAction(i, { config: { ...a.config, [f.key]: v } })}
                      />
                    </FieldRow>
                  ))}
                  <FieldRow
                    label="Data path (optional)"
                    description="Dotted path into the input data for this action"
                  >
                    <TextInput
                      value={a.dataPath}
                      onChange={(v) => updateAction(i, { dataPath: v || undefined })}
                      placeholder="drivers[0]"
                    />
                  </FieldRow>
                </div>
              );
            })}
          </div>

          <div>
            <div className="text-[11px] font-semibold uppercase tracking-wide text-neutral-500 dark:text-neutral-400 mb-1">
              Add action
            </div>
            <div className="flex flex-wrap gap-1">
              {selectedIntegration.capabilities.map((c) => (
                <button
                  key={c.key}
                  type="button"
                  onClick={() => addAction(c.key)}
                  className="inline-flex items-center gap-1 rounded-full border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 px-2 py-0.5 text-[11px] text-neutral-700 dark:text-neutral-300 hover:border-primary-300 dark:hover:border-primary-500/30 hover:text-primary-700 dark:hover:text-primary-300"
                >
                  <PlusIcon className="h-3 w-3" />
                  {c.label}
                </button>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function CapabilityField(props: {
  field: { type: string; options?: { value: string; label: string }[]; multiline?: boolean };
  value: unknown;
  onChange: (v: unknown) => void;
}) {
  const { field, value, onChange } = props;
  if (field.type === 'enum' && field.options) {
    return (
      <Select value={value as string | undefined} onChange={onChange} options={field.options} />
    );
  }
  if (field.type === 'boolean') {
    return <BooleanInput value={value as boolean | undefined} onChange={onChange} />;
  }
  if (field.type === 'number') {
    return <NumberInput value={value as number | undefined} onChange={(v) => onChange(v)} />;
  }
  if (field.type === 'json') {
    return (
      <TextInput
        value={
          typeof value === 'string'
            ? value
            : value === undefined
              ? ''
              : JSON.stringify(value, null, 2)
        }
        onChange={onChange}
        multiline
      />
    );
  }
  return (
    <TextInput
      value={(value as string | undefined) ?? ''}
      onChange={onChange}
      multiline={field.multiline}
    />
  );
}

// ----- Output run-mode switch (shared by OUTPUT_DOCUMENT + OUTPUT_INTEGRATION) -----

/**
 * Segmented control that lets the user pick how an output node reacts to a
 * fan-out upstream:
 *   - "batch":    consume the whole list, produce one artifact
 *   - "per-item": iterate the list, produce one artifact per item
 *
 * Switching modes also toggles `node.forEach` so the canvas, validation, and
 * layout reflect the semantic choice without a separate toggle.
 */
export function OutputRunModeSwitch(props: { node: DefinitionNode }) {
  const { node } = props;
  const { state, dispatch } = useBuilder();
  const mode = outputRunMode(node);
  const upstreamIsList = hasUpstreamList(state, node);
  const artifactNoun =
    node.taskKey === 'OUTPUT_DOCUMENT'
      ? { single: 'file', plural: 'files' }
      : { single: 'run', plural: 'runs' };

  const setMode = (next: 'batch' | 'per-item') => {
    if (next === mode) return;
    dispatch({ type: 'set-config', nodeId: node.id, configKey: 'runMode', value: next });
    if (next === 'per-item') {
      const fe = firstEnumerableBinding(state, node);
      if (fe) {
        dispatch({ type: 'set-for-each', nodeId: node.id, forEach: fe });
      }
    } else {
      dispatch({ type: 'set-for-each', nodeId: node.id, forEach: null });
    }
  };

  return (
    <div className="space-y-1.5">
      <div className="text-[11px] font-semibold uppercase tracking-wide text-neutral-500 dark:text-neutral-400">
        Run mode
      </div>
      <div className="flex gap-1 rounded-md bg-neutral-100 dark:bg-neutral-700 p-1">
        <ModeButton
          active={mode === 'batch'}
          onClick={() => setMode('batch')}
          label={
            node.taskKey === 'OUTPUT_DOCUMENT' ? 'One combined file' : 'One run for the whole list'
          }
          sub={
            node.taskKey === 'OUTPUT_DOCUMENT'
              ? 'Accept a list, merge it, write one file'
              : 'Each action receives the whole list once'
          }
        />
        <ModeButton
          active={mode === 'per-item'}
          onClick={() => setMode('per-item')}
          label={`One ${artifactNoun.single} per item`}
          sub={
            node.taskKey === 'OUTPUT_DOCUMENT'
              ? 'Iterate the upstream list, render a file per item'
              : 'Run every action once per upstream list item'
          }
        />
      </div>
      {mode === 'per-item' && !upstreamIsList && (
        <div className="rounded-md bg-amber-50 dark:bg-amber-500/10 p-2 text-[11px] text-amber-700 dark:text-amber-300">
          Connect a list source (e.g. an Extract output) as the data input — per-item needs
          something to iterate over.
        </div>
      )}
      {mode === 'batch' && upstreamIsList && (
        <div className="text-[11px] text-neutral-500 dark:text-neutral-400">
          The whole upstream list flows into this step and produces a single {artifactNoun.single}.
        </div>
      )}
      {mode === 'batch' && !upstreamIsList && (
        <div className="text-[11px] text-neutral-400 dark:text-neutral-500">
          When an upstream list is connected, it will be consumed as a whole.
        </div>
      )}
    </div>
  );
}

function ModeButton(props: { active: boolean; onClick: () => void; label: string; sub: string }) {
  return (
    <button
      type="button"
      onClick={props.onClick}
      className={clsx(
        'flex-1 rounded px-2 py-1.5 text-left transition',
        props.active
          ? 'bg-white dark:bg-neutral-800 shadow-sm ring-1 ring-neutral-200 dark:ring-neutral-700'
          : 'text-neutral-600 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-white'
      )}
    >
      <div
        className={clsx(
          'text-xs font-semibold',
          props.active
            ? 'text-neutral-900 dark:text-white'
            : 'text-neutral-700 dark:text-neutral-300'
        )}
      >
        {props.label}
      </div>
      <div className="text-[10px] leading-tight text-neutral-500 dark:text-neutral-400">
        {props.sub}
      </div>
    </button>
  );
}

// ----- Re-export for inspector to discover which primitive needs bespoke forms -----

export function needsBespokeSubform(node: DefinitionNode): boolean {
  return (
    node.taskKey === 'EXTRACT' ||
    node.taskKey === 'OUTPUT_INTEGRATION' ||
    node.taskKey === 'OUTPUT_DOCUMENT' ||
    getPrimitive(node.taskKey).category === 'input'
  );
}
